// src/pages/auth/CompleteProfilePage.tsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { RootState } from "../redux/store";
import {
  publicSendOtp,
  publicVerifyOtp,
  publicRegister,
} from "../redux/slices/publicAuthSlice";

const CompleteProfilePage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const { loading } = useSelector((state: RootState) => state.publicAuth);

  const savedMobile = localStorage.getItem("public_mobile") || "";

  const [step, setStep] = useState<"mobile" | "otp" | "profile">(
    savedMobile ? "profile" : "mobile"
  );

  const [mobile, setMobile] = useState(savedMobile);
  const [otp, setOtp] = useState("");

  const [formData, setFormData] = useState({
    phone_number: savedMobile,
    user_fullname: "",
    email: "",
    dob: "",
  });

const handleSendOtp = async () => {
  if (!mobile || mobile.length < 10) {
    toast.error("Enter valid mobile number");
    return;
  }

  const res = await dispatch(publicSendOtp(mobile));

  if (publicSendOtp.fulfilled.match(res)) {
    const data = res.payload;

    // USER ALREADY REGISTERED - DO NOT LOGIN
    if (data?.user_exists) {
      toast.info("User already registered. Please login.");

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("server_otp");
      localStorage.removeItem("login_response");

      navigate("/login");
      return;
    }

    // NEW USER REGISTRATION FLOW
    localStorage.setItem("public_mobile", mobile);
    toast.success("OTP sent successfully");
    setStep("otp");
  } else {
    toast.error(res.payload || "Failed to send OTP");
  }
};

  const handleVerifyOtp = async () => {
    if (!otp) {
      toast.error("Enter OTP");
      return;
    }

    const res = await dispatch(
      publicVerifyOtp({
        phone_number: mobile,
        otp,
      })
    );

    if (publicVerifyOtp.fulfilled.match(res)) {
      localStorage.setItem("public_mobile", mobile);

      setFormData((prev) => ({
        ...prev,
        phone_number: mobile,
      }));

      toast.success("OTP verified");
      setStep("profile");
    } else {
      toast.error(res.payload || "OTP verification failed");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      phone_number: mobile,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    if (!mobile) {
      toast.error("Mobile number missing");
      setStep("mobile");
      return;
    }

    if (!formData.user_fullname) {
      toast.error("Enter full name");
      return;
    }

    const res = await dispatch(
      publicRegister({
        ...formData,
        phone_number: mobile,
      })
    );

    if (publicRegister.fulfilled.match(res)) {
      toast.success("Registration completed");
      navigate("/join-club");
    } else {
      toast.error(res.payload || "Registration failed");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.bgCircleOne} />
      <div style={styles.bgCircleTwo} />
      <div style={styles.bgCircleThree} />

      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <div style={styles.logoBox}>
            <img
              src="/assets/images/bharat_sangam.png"
              alt="Bharat Sangam"
              style={styles.logo}
            />
          </div>
        </div>

        <div style={styles.headerBox}>
          <h1 style={styles.title}>
            {step === "mobile" && "Enter Mobile Number"}
            {step === "otp" && "Verify OTP"}
            {step === "profile" && "Complete Your Profile"}
          </h1>

          <p style={styles.subTitle}>
            {step === "mobile" &&
              "Enter your mobile number to continue registration"}
            {step === "otp" && "Enter the OTP sent to your mobile number"}
            {step === "profile" &&
              "Please provide your basic details to complete registration"}
          </p>
        </div>

        {step !== "mobile" && (
          <div style={styles.mobileBadge}>
            <span style={styles.mobileIcon}>📱</span>
            <span>{mobile}</span>
          </div>
        )}

        <div style={styles.formBox}>
          {step === "mobile" && (
            <>
              <label style={styles.label}>Mobile Number</label>
              <input
                name="mobile"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                style={styles.input}
                maxLength={10}
              />

              <button
                onClick={handleSendOtp}
                disabled={loading}
                style={{
                  ...styles.button,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Sending..." : "Send OTP →"}
              </button>
            </>
          )}

          {step === "otp" && (
            <>
              <label style={styles.label}>OTP</label>
              <input
                name="otp"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                style={styles.input}
                maxLength={6}
              />

              <button
                onClick={handleVerifyOtp}
                disabled={loading}
                style={{
                  ...styles.button,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Verifying..." : "Verify OTP →"}
              </button>

              <button
                type="button"
                onClick={() => setStep("mobile")}
                style={styles.backButton}
              >
                Change mobile number
              </button>
            </>
          )}

          {step === "profile" && (
            <>
              <label style={styles.label}>Mobile Number</label>
              <input
                value={mobile}
                readOnly
                style={{
                  ...styles.input,
                  background: "#f8fafc",
                  color: "#64748b",
                  cursor: "not-allowed",
                }}
              />

              <label style={styles.label}>Full Name</label>
              <input
                name="user_fullname"
                placeholder="Enter full name"
                value={formData.user_fullname}
                onChange={handleChange}
                style={styles.input}
              />

              <label style={styles.label}>Email</label>
              <input
                name="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
              />

              <label style={styles.label}>DOB</label>
              <input
                name="dob"
                type="date"
                value={formData.dob}
                onChange={handleChange}
                style={styles.input}
              />

              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  ...styles.button,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Submitting..." : "Complete Registration →"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: any = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8fbff",
    position: "relative",
    overflow: "hidden",
    padding: "20px",
  },
  bgCircleOne: {
    position: "absolute",
    width: "260px",
    height: "260px",
    borderRadius: "50%",
    background: "#dbeafe",
    top: "-80px",
    left: "-80px",
  },
  bgCircleTwo: {
    position: "absolute",
    width: "220px",
    height: "220px",
    borderRadius: "50%",
    background: "#e0f2fe",
    bottom: "-70px",
    right: "-70px",
  },
  bgCircleThree: {
    position: "absolute",
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    background: "#f0f9ff",
    top: "20%",
    right: "12%",
  },
  card: {
    width: "100%",
    maxWidth: "430px",
    background: "#ffffff",
    borderRadius: "24px",
    padding: "32px",
    boxShadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
    position: "relative",
    zIndex: 2,
  },
  logoWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "18px",
  },
  logoBox: {
    width: "82px",
    height: "82px",
    borderRadius: "24px",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 25px rgba(2, 132, 199, 0.14)",
  },
  logo: {
    width: "64px",
    height: "64px",
    objectFit: "contain",
  },
  headerBox: {
    textAlign: "center",
    marginBottom: "22px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: 700,
    color: "#0f172a",
  },
  subTitle: {
    marginTop: "8px",
    fontSize: "14px",
    color: "#64748b",
    lineHeight: 1.5,
  },
  mobileBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    background: "#eff6ff",
    color: "#2563eb",
    padding: "10px 14px",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "18px",
  },
  mobileIcon: {
    fontSize: "16px",
  },
  formBox: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  label: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#334155",
    marginTop: "6px",
  },
  input: {
    width: "100%",
    height: "46px",
    borderRadius: "12px",
    border: "1px solid #dbe3ef",
    padding: "0 14px",
    fontSize: "14px",
    outline: "none",
    color: "#0f172a",
    background: "#ffffff",
    boxSizing: "border-box",
  },
  button: {
    width: "100%",
    height: "48px",
    borderRadius: "14px",
    border: "none",
    background: "linear-gradient(135deg, #2563eb, #0284c7)",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: 700,
    marginTop: "16px",
  },
  backButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontSize: "14px",
    fontWeight: 600,
    marginTop: "8px",
    cursor: "pointer",
  },
};

export default CompleteProfilePage;